import { TopicsData } from "@components/Blocks/FollowTrendingTopics/FollowTrendingTopics";
import { HandleFollowTopic, HandleUnFollowTopic } from "@helper/baasHelper";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useBHCustomEventAnalyticsCall } from "./useAnalyticsCall/useBHCustomEventAnalyticsCall";

type UseFollowTrendingTagsReturn = {
  topics: TopicsData[];
  visitorId: string;
  toggleTopic: (topicId: string, topicTitle: string) => Promise<void>;
  shuffleTopics: () => Promise<void>;
  isActive: (topicId: string) => boolean;
  isFollowing: (topicId: string) => boolean;
  isSelected: (topicId: string) => boolean;
  selectedCount: number;
  followedCount: number;
  loadingTopicId: string | null;
  showToast: boolean;
  toastCount: number;
};

type UseFollowTrendingTagsProps = {
  topics?: TopicsData[];
  visitorId: string;
  isLoggedIn: boolean;
  followedIds: string[];
  userKeywords: string;
  fetchMoreTopics: () => Promise<TopicsData[]>;
  /** Number of topics to show per page. 0 (default) shows all topics. */
  pageSize?: number;
};

function getCircularSlice(
  arr: TopicsData[] | undefined,
  start: number,
  size: number,
): TopicsData[] {
  if (!arr || arr.length === 0 || size <= 0) return [];

  const effectiveSize = Math.min(size, arr.length);
  const normalizedStart = ((start % arr.length) + arr.length) % arr.length;

  return Array.from(
    { length: effectiveSize },
    (_, i) => arr[(normalizedStart + i) % arr.length],
  );
}

type SelectedTopic = {
  topicId: string;
  topicTitle: string;
};

const STORAGE_KEY = "selectedTopics";

export function useFollowTrendingTags({
  topics = [],
  visitorId,
  isLoggedIn,
  followedIds,
  userKeywords,
  fetchMoreTopics,
  pageSize = 0,
}: UseFollowTrendingTagsProps): UseFollowTrendingTagsReturn {
  // ================= GUEST SELECTION =================
  const [selectedTopics, setSelectedTopics] = useState<SelectedTopic[]>([]);

  // ================= DISPLAY =================
  const offsetRef = useRef(0);
  const [displayTopics, setDisplayTopics] = useState<TopicsData[]>(
    pageSize > 0 ? getCircularSlice(topics, 0, pageSize) : topics,
  );

  // ================= LOADING =================
  const [loadingTopicId, setLoadingTopicId] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastCount, setToastCount] = useState(0);

  // ================ TRIGGER TOAST ==============
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const triggerToast = useCallback(() => {
    setShowToast(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setShowToast(false);
    }, 2500);
  }, []);

  useEffect(() => {
    if (!showToast) setToastCount(0);
  }, [showToast]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // ================= ANALYTICS =================
  const bhCustomEvent = useBHCustomEventAnalyticsCall();
  const pushAnalyticsEvent = useCallback(
    (label: string, action: "follow" | "unfollow" | "click") => {
      bhCustomEvent(
        "mybh",
        action,
        label,
        undefined,
        undefined,
        undefined,
        undefined,
        userKeywords,
      );
    },
    [bhCustomEvent, userKeywords],
  );

  // ================= LOAD GUEST =================
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setSelectedTopics(JSON.parse(saved) as SelectedTopic[]);
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // ================= PERSIST GUEST =================
  useEffect(() => {
    if (isLoggedIn) return;

    try {
      if (selectedTopics.length === 0) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedTopics));
      }
    } catch {
      // storage disabled or quota exceeded — silently ignore
    }
  }, [selectedTopics, isLoggedIn]);

  // ================= FLUSH ON LOGIN =================
  const hasFlushedRef = useRef(false);
  const selectedTopicsRef = useRef(selectedTopics);
  useEffect(() => {
    selectedTopicsRef.current = selectedTopics;
  }, [selectedTopics]);

  useEffect(() => {
    if (!isLoggedIn || hasFlushedRef.current) return;

    const topicsToFlush = selectedTopicsRef.current;
    if (topicsToFlush.length === 0) return;

    hasFlushedRef.current = true;

    void Promise.allSettled(
      topicsToFlush.map((topic) =>
        HandleFollowTopic(topic.topicId, visitorId, topic.topicTitle, () => {}),
      ),
    ).then((results) => {
      const successResults = results
        .map((result, index) => ({ result, topic: topicsToFlush[index] }))
        .filter(({ result }) => result.status === "fulfilled" && result.value);

      const count = successResults.length;

      successResults.forEach(({ topic }) => {
        pushAnalyticsEvent(`topic::${topic.topicTitle}`, "follow");
      });

      if (count > 0) {
        setToastCount(count);
        triggerToast();
      }

      setSelectedTopics([]);
      localStorage.removeItem(STORAGE_KEY);
    });
  }, [isLoggedIn, visitorId, triggerToast, pushAnalyticsEvent]);

  // ================= SYNC TOPICS =================
  useEffect(() => {
    offsetRef.current = 0;
    setDisplayTopics(
      pageSize > 0 ? getCircularSlice(topics, 0, pageSize) : topics,
    );
  }, [topics, pageSize]);

  // ================= HELPERS =================
  const isFollowing = useCallback(
    (topicId: string) => followedIds.includes(topicId),
    [followedIds],
  );

  const isSelected = useCallback(
    (topicId: string) => selectedTopics.some((t) => t.topicId === topicId),
    [selectedTopics],
  );

  const isActive = useCallback(
    (topicId: string) =>
      isLoggedIn ? isFollowing(topicId) : isSelected(topicId),
    [isLoggedIn, isFollowing, isSelected],
  );

  // ================= TOGGLE =================
  const requestInFlight = useRef<Set<string>>(new Set());

  const toggleTopic = useCallback(
    async (topicId: string, topicTitle: string) => {
      if (!isLoggedIn) {
        // Guest mode
        setSelectedTopics((prev) => {
          const exists = prev.some((t) => t.topicId === topicId);

          if (exists) {
            return prev.filter((t) => t.topicId !== topicId);
          }

          return [...prev, { topicId, topicTitle }];
        });
        return;
      }

      if (!topicId || requestInFlight.current.has(topicId)) return;

      requestInFlight.current.add(topicId);
      setLoadingTopicId(topicId);

      try {
        const alreadyFollowing = isFollowing(topicId);
        const isFollow = !alreadyFollowing;

        const handler = isFollow ? HandleFollowTopic : HandleUnFollowTopic;
        const action = isFollow ? "follow" : "unfollow";

        const isSuccess = await handler(
          topicId,
          visitorId,
          topicTitle,
          () => {},
        );

        if (!isSuccess) return;

        if (isFollow) {
          setToastCount((prev) => prev + 1);
          triggerToast();
        }

        const eventLabel = `topic::${topicTitle}`;
        pushAnalyticsEvent(eventLabel, action);
      } catch (err) {
        console.error("Toggle failed", err);
      } finally {
        requestInFlight.current.delete(topicId);
        setLoadingTopicId(null);
      }
    },
    [isLoggedIn, isFollowing, visitorId, triggerToast, pushAnalyticsEvent],
  );

  // ================= DERIVED ORDER =================
  const derivedTopics = useMemo(() => {
    return displayTopics.map((t) => ({
      ...t,
      isFollowing: followedIds.includes(t.topicId),
    }));
  }, [displayTopics, followedIds]);

  // ================= SHUFFLE =================
  const shuffleTopics = useCallback(async () => {
    if (pageSize > 0 && topics.length > 0) {
      const nextOffset = (offsetRef.current + pageSize) % topics.length;
      offsetRef.current = nextOffset;
      setDisplayTopics(getCircularSlice(topics, nextOffset, pageSize));
      pushAnalyticsEvent("shuffle", "click");
      return;
    }

    // Original behaviour when no pageSize
    const newTopics = await fetchMoreTopics();

    const activeSet = new Set(
      isLoggedIn ? followedIds : selectedTopics.map((t) => t.topicId),
    );

    const active = displayTopics.filter((t) => activeSet.has(t.topicId));

    const existingIds = new Set(displayTopics.map((t) => t.topicId));

    const newOnes = newTopics
      .filter((t) => !activeSet.has(t.topicId) && !existingIds.has(t.topicId))
      .map((t) => ({
        ...t,
        isFollowing: followedIds.includes(t.topicId),
      }));

    setDisplayTopics([...active, ...newOnes]);
    pushAnalyticsEvent("shuffle", "click");
  }, [
    pageSize,
    topics,
    fetchMoreTopics,
    displayTopics,
    followedIds,
    selectedTopics,
    isLoggedIn,
    pushAnalyticsEvent,
  ]);

  return {
    topics: derivedTopics,
    toggleTopic,
    shuffleTopics,
    isActive,
    isFollowing,
    isSelected,
    selectedCount: selectedTopics.length,
    followedCount: followedIds.length,
    loadingTopicId,
    showToast,
    toastCount,
    visitorId,
  };
}
